import { useState, useCallback } from 'react';
import { useSnackbarNotification } from '@/hooks/useSnackbarNotification';
import { useTokenRoles } from '@/hooks/useTokenRoles';
import { getMembersApi } from '@/services/bolApiClient';
import { Member } from '@/api/bol';
import { extractErrorMessage } from '@/utils/errorUtils';

export interface UseMembersReturn {
  members: Member[];
  membersLoading: boolean;
  filteredMembers: (Member & { disabled: boolean })[];
  fetchMembers: () => Promise<void>;
}

export const useMembers = (): UseMembersReturn => {
  const [members, setMembers] = useState<Member[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  
  const { showError } = useSnackbarNotification();
  const { memberIdentifiers } = useTokenRoles();

  const fetchMembers = useCallback(async () => {
    setMembersLoading(true);
    try {
      const api = getMembersApi();
      
      const response = await api.listMembers({
        limit: 100
      }); // Fetch more members for dropdown
      
      setMembers(response.results || []);
    } catch (err: unknown) {
      console.error('Error fetching members:', err);
      const errorMessage = await extractErrorMessage(err, 'Failed to fetch members');
      showError(errorMessage);
    } finally {
      setMembersLoading(false);
    }
  }, [showError]);

  // Filter members based on normalized member identifiers from the token
  const filteredMembers = useCallback((): (Member & { disabled: boolean })[] => {
    if (!members.length) return [];
    
    if (!memberIdentifiers || memberIdentifiers.length === 0) {
      return members.map(member => ({ ...member, disabled: true }));
    }
    
    return members.map(member => {
      const hasAccess = memberIdentifiers.includes(member.identifier);
      return { ...member, disabled: !hasAccess };
    });
  }, [memberIdentifiers, members]);

  return {
    members,
    membersLoading,
    filteredMembers: filteredMembers(),
    fetchMembers
  };
};